/**
 * Dashboard Page - Overview of Filmssavn content
 */

import { createNavigation } from '../components/navigation.js';
import { api } from '../lib/api.js';
import { getCategoryDisplay, t } from '../lib/i18n.js';
import { getCurrentUser } from '../lib/keycloak.js';

const numberFormatter = new Intl.NumberFormat('en-GB');
const moneyFormatter = new Intl.NumberFormat('en-GB', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

function escapeHtml(value) {
  return String(value ?? '').replace(/[&<>"']/g, (char) => {
    const entities = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;',
    };
    return entities[char];
  });
}

function formatNumber(value) {
  return numberFormatter.format(Number(value || 0));
}

function formatMoney(value) {
  return `${moneyFormatter.format(Number(value || 0))} DKK`;
}

function formatDurationSeconds(value) {
  const seconds = Math.max(0, Math.floor(Number(value || 0)));
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }

  return `${minutes}m`;
}

function renderMetric(label, value, detail = '') {
  return `
    <div class="stats-metric">
      <span class="stats-metric-label">${label}</span>
      <strong class="stats-metric-value">${value}</strong>
      ${detail ? `<span class="stats-metric-detail">${detail}</span>` : ''}
    </div>
  `;
}

function renderMonthlyRows(rows) {
  if (!rows.length) {
    return `
      <tr>
        <td colspan="5" class="text-center">${t('stats_no_paid_rentals')}</td>
      </tr>
    `;
  }

  return rows
    .map(
      (row) => `
        <tr>
          <td><strong>${escapeHtml(row.month)}</strong></td>
          <td class="text-right">${formatNumber(row.paidRentals)}</td>
          <td class="text-right">${formatNumber(row.distinctPaidFilms)}</td>
          <td class="text-right">${formatNumber(row.payingUsers)}</td>
          <td class="text-right">${formatMoney(row.grossDkk)}</td>
        </tr>
      `,
    )
    .join('');
}

function renderFilmRows(rows) {
  if (!rows.length) {
    return `
      <tr>
        <td colspan="6" class="text-center">${t('stats_no_paid_rentals')}</td>
      </tr>
    `;
  }

  return rows
    .map(
      (row) => `
        <tr>
          <td>
            <strong>${escapeHtml(row.title)}</strong>
            <div class="stats-row-subtitle">${escapeHtml(row.videoId)}</div>
          </td>
          <td class="text-right">${formatNumber(row.paidRentals)}</td>
          <td class="text-right">${formatNumber(row.payingUsers)}</td>
          <td class="text-right">${formatNumber(row.repeatPaidRentals)}</td>
          <td class="text-right">${formatMoney(row.grossDkk)}</td>
          <td>${escapeHtml(row.lastPaidAt || '-')}</td>
        </tr>
      `,
    )
    .join('');
}

function renderFreeMonthlyRows(rows) {
  if (!rows.length) {
    return `
      <tr>
        <td colspan="5" class="text-center">${t('stats_no_free_activity')}</td>
      </tr>
    `;
  }

  return rows
    .map(
      (row) => `
        <tr>
          <td><strong>${escapeHtml(row.month)}</strong></td>
          <td class="text-right">${formatNumber(row.freeViews)}</td>
          <td class="text-right">${formatNumber(row.uniqueViewers)}</td>
          <td class="text-right">${formatNumber(row.completions)}</td>
          <td class="text-right">${formatDurationSeconds(row.watchTimeSeconds)}</td>
        </tr>
      `,
    )
    .join('');
}

function renderFreeFilmRows(rows) {
  if (!rows.length) {
    return `
      <tr>
        <td colspan="6" class="text-center">${t('stats_no_free_activity')}</td>
      </tr>
    `;
  }

  return rows
    .map(
      (row) => `
        <tr>
          <td>
            <strong>${escapeHtml(row.title)}</strong>
            <div class="stats-row-subtitle">${escapeHtml(row.videoId)}</div>
          </td>
          <td class="text-right">${formatNumber(row.freeViews)}</td>
          <td class="text-right">${formatNumber(row.uniqueViewers)}</td>
          <td class="text-right">${formatNumber(row.completions)}</td>
          <td class="text-right">${formatDurationSeconds(row.watchTimeSeconds)}</td>
          <td>${escapeHtml(row.lastViewedAt || '-')}</td>
        </tr>
      `,
    )
    .join('');
}

function renderRentalStats(stats) {
  const content = document.getElementById('rental-stats-content');
  const period = document.getElementById('rental-stats-period');
  const totals = stats?.totals || {};
  const monthly = stats?.monthly || [];
  const films = stats?.films || [];
  const freeTotals = stats?.free?.totals || {};
  const freeMonthly = stats?.free?.monthly || [];
  const freeFilms = stats?.free?.films || [];

  period.textContent = `${stats.period?.label || 'YTD'} · ${stats.period?.endAt || ''}`;
  content.innerHTML = `
    <div class="stats-section-heading">
      <h3>${t('dashboard_paid_stats_title')}</h3>
    </div>

    <div class="stats-metric-grid">
      ${renderMetric(t('stats_paid_rentals'), formatNumber(totals.paidRentalTransactions))}
      ${renderMetric(t('stats_unique_film_payments'), formatNumber(totals.uniqueUserFilmPairs))}
      ${renderMetric(t('stats_gross_revenue'), formatMoney(totals.grossAmountDkk))}
      ${renderMetric(t('stats_paying_users'), formatNumber(totals.payingUsers))}
      ${renderMetric(t('stats_distinct_paid_films'), formatNumber(totals.distinctPaidFilms))}
      ${renderMetric(t('stats_repeat_rentals'), formatNumber(totals.repeatPaidRentals))}
      ${renderMetric(t('stats_average_transaction'), formatMoney(totals.averageTransactionDkk))}
      ${renderMetric(t('stats_refunds'), formatNumber(totals.refundedRentals))}
    </div>

    <div class="stats-table-grid">
      <div class="stats-table-block">
        <div class="stats-block-header">
          <h3>${t('stats_monthly_breakdown')}</h3>
        </div>
        <div class="table-container">
          <table class="table stats-table">
            <thead>
              <tr>
                <th>${t('stats_month')}</th>
                <th class="text-right">${t('stats_paid_rentals')}</th>
                <th class="text-right">${t('stats_paid_films')}</th>
                <th class="text-right">${t('stats_users')}</th>
                <th class="text-right">${t('stats_gross')}</th>
              </tr>
            </thead>
            <tbody>
              ${renderMonthlyRows(monthly)}
            </tbody>
          </table>
        </div>
      </div>

      <div class="stats-table-block">
        <div class="stats-block-header">
          <h3>${t('stats_film_breakdown')}</h3>
        </div>
        <div class="table-container">
          <table class="table stats-table">
            <thead>
              <tr>
                <th>${t('stats_film')}</th>
                <th class="text-right">${t('stats_paid_rentals')}</th>
                <th class="text-right">${t('stats_users')}</th>
                <th class="text-right">${t('stats_repeat_rentals')}</th>
                <th class="text-right">${t('stats_gross')}</th>
                <th>${t('stats_last_paid')}</th>
              </tr>
            </thead>
            <tbody>
              ${renderFilmRows(films)}
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <div class="stats-section-heading stats-section-heading-spaced">
      <h3>${t('dashboard_free_stats_title')}</h3>
    </div>

    <div class="stats-metric-grid">
      ${renderMetric(t('stats_free_films'), formatNumber(freeTotals.availableFreeFilms))}
      ${renderMetric(t('stats_free_views'), formatNumber(freeTotals.freeViewEvents))}
      ${renderMetric(t('stats_unique_viewers'), formatNumber(freeTotals.freeUniqueViewers))}
      ${renderMetric(t('stats_watched_free_films'), formatNumber(freeTotals.watchedFreeFilms))}
      ${renderMetric(t('stats_completions'), formatNumber(freeTotals.freeCompletions))}
      ${renderMetric(t('stats_watch_time'), formatDurationSeconds(freeTotals.freeWatchTimeSeconds))}
    </div>

    <div class="stats-table-grid">
      <div class="stats-table-block">
        <div class="stats-block-header">
          <h3>${t('stats_free_monthly_breakdown')}</h3>
        </div>
        <div class="table-container">
          <table class="table stats-table">
            <thead>
              <tr>
                <th>${t('stats_month')}</th>
                <th class="text-right">${t('stats_views')}</th>
                <th class="text-right">${t('stats_viewers')}</th>
                <th class="text-right">${t('stats_completions')}</th>
                <th class="text-right">${t('stats_watch_time')}</th>
              </tr>
            </thead>
            <tbody>
              ${renderFreeMonthlyRows(freeMonthly)}
            </tbody>
          </table>
        </div>
      </div>

      <div class="stats-table-block">
        <div class="stats-block-header">
          <h3>${t('stats_free_film_breakdown')}</h3>
        </div>
        <div class="table-container">
          <table class="table stats-table">
            <thead>
              <tr>
                <th>${t('stats_film')}</th>
                <th class="text-right">${t('stats_views')}</th>
                <th class="text-right">${t('stats_viewers')}</th>
                <th class="text-right">${t('stats_completions')}</th>
                <th class="text-right">${t('stats_watch_time')}</th>
                <th>${t('stats_last_viewed')}</th>
              </tr>
            </thead>
            <tbody>
              ${renderFreeFilmRows(freeFilms)}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  `;
}

async function loadRentalStats() {
  const content = document.getElementById('rental-stats-content');

  try {
    const stats = await api.getRentalStats();
    renderRentalStats(stats);
  } catch (error) {
    console.error('Failed to load rental stats:', error);
    content.innerHTML = `
      <div class="stats-empty">
        <h3>${t('stats_load_error')}</h3>
      </div>
    `;
  }
}

async function loadVideoOverview() {
  try {
    const allVideos = await api.getVideos();

    // Filter out deleted videos
    const videos = (allVideos || []).filter((v) => v.state !== 'deleted');

    // Calculate stats
    const totalVideos = videos.length;
    const ready = videos.filter((v) => v.state === 'ready').length;
    const encoding = videos.filter((v) => v.state === 'encoding' || v.state === 'uploaded').length;

    document.getElementById('total-videos').textContent = totalVideos;
    document.getElementById('ready-count').textContent = ready;
    document.getElementById('encoding-count').textContent = encoding;

    // Show recent videos
    const recentContainer = document.getElementById('recent-videos');
    recentContainer.innerHTML = '';

    if (videos.length > 0) {
      const table = document.createElement('div');
      table.className = 'table-container';
      table.innerHTML = `
        <table class="table">
          <thead>
            <tr>
              <th>${t('form_original_title')}</th>
              <th>Status</th>
              <th>${t('form_category')}</th>
              <th>${t('manage_uploaded_by')}</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            ${videos
              .slice(0, 10)
              .map(
                (video) => `
                  <tr>
                    <td><strong>${escapeHtml(video.originalTitle || video.title || video.fileName || 'Untitled')}</strong></td>
                    <td>
                      <span class="status-badge status-${escapeHtml(video.state || 'ready')}">
                        ${escapeHtml(video.state || 'Ready')}
                      </span>
                    </td>
                    <td>
                      ${video.category ? escapeHtml(getCategoryDisplay(video.category)) : '-'}
                      ${video.isFaroese ? '<br><span style="color: var(--color-faroese-amber); font-size: 0.875rem;">Føroyskt</span>' : ''}
                    </td>
                    <td style="color: var(--color-cloud-grey);">
                      ${escapeHtml(video.ownerEmail || video.ownerId || 'Unknown')}
                    </td>
                    <td>
                      <div class="flex flex-gap-sm">
                        <a href="/edit/${encodeURIComponent(video.videoId)}" class="btn btn-small btn-secondary" data-link>
                          Edit
                        </a>
                      </div>
                    </td>
                  </tr>
                `,
              )
              .join('')}
          </tbody>
        </table>
      `;
      recentContainer.appendChild(table);
    } else {
      recentContainer.innerHTML = `
        <div style="text-align: center; padding: 40px; color: var(--color-cloud-grey);">
          <p>${t('dashboard_no_videos')}</p>
          <a href="/upload" class="btn btn-primary mt-md" data-link>${t('upload_button')}</a>
        </div>
      `;
    }
  } catch (error) {
    console.error('Failed to load dashboard:', error);
    document.getElementById('total-videos').textContent = '-';
    document.getElementById('ready-count').textContent = '-';
    document.getElementById('encoding-count').textContent = '-';
  }
}

export async function renderDashboard() {
  const app = document.getElementById('app');
  app.innerHTML = '';

  app.appendChild(await createNavigation());
  const currentUser = await getCurrentUser();
  const canViewGlobalStats = currentUser?.role === 'admin';

  const main = document.createElement('main');
  main.className = 'admin-main';
  main.innerHTML = `
    <div class="container">
      <div class="mb-lg flex flex-between flex-center">
        <h1>${t('dashboard_title')}</h1>
      </div>

      <div class="video-grid mb-lg" id="stats-grid">
        <div class="card">
          <div class="card-header">
            <h3 class="card-title">${t('dashboard_total_videos')}</h3>
          </div>
          <div style="font-size: 48px; font-weight: 800; color: var(--color-nordic-blue);" id="total-videos">
            <div class="spinner" style="margin: 20px auto;"></div>
          </div>
        </div>

        <div class="card">
          <div class="card-header">
            <h3 class="card-title">${t('dashboard_ready')}</h3>
          </div>
          <div style="font-size: 48px; font-weight: 800; color: var(--color-faroese-amber);" id="ready-count">
            <div class="spinner" style="margin: 20px auto;"></div>
          </div>
        </div>

        <div class="card">
          <div class="card-header">
            <h3 class="card-title">${t('dashboard_encoding')}</h3>
          </div>
          <div style="font-size: 48px; font-weight: 800; color: var(--color-info);" id="encoding-count">
            <div class="spinner" style="margin: 20px auto;"></div>
          </div>
        </div>
      </div>

      ${
        canViewGlobalStats
          ? `
            <section class="card stats-panel mb-lg" aria-labelledby="rental-stats-title">
              <div class="card-header stats-panel-header">
                <div>
                  <h2 class="card-title" id="rental-stats-title">${t('dashboard_stats_title')}</h2>
                  <p class="card-subtitle">${t('dashboard_stats_subtitle')}</p>
                </div>
                <span class="stats-period" id="rental-stats-period">${t('msg_loading')}</span>
              </div>
              <div id="rental-stats-content" class="stats-loading">
                <div class="spinner"></div>
              </div>
            </section>
          `
          : ''
      }

      <div class="card mb-lg">
        <div class="card-header">
          <h2 class="card-title">Quick Actions</h2>
        </div>
        <div class="flex flex-gap-md">
          <a href="/upload" class="btn btn-primary" data-link>
            Upload New Video
          </a>
          <a href="/manage" class="btn btn-secondary" data-link>
            Manage Videos
          </a>
        </div>
      </div>

      <div class="card">
        <div class="card-header">
          <h2 class="card-title">${t('dashboard_recent_uploads')}</h2>
          <p class="card-subtitle">Latest uploads</p>
        </div>
        <div id="recent-videos">
          <div class="flex" style="justify-content: center; padding: 40px;">
            <div class="spinner"></div>
          </div>
        </div>
      </div>
    </div>
  `;

  app.appendChild(main);

  await Promise.all([loadVideoOverview(), canViewGlobalStats ? loadRentalStats() : Promise.resolve()]);
}
